// Consent Management System
// Handles user consent for data collection (GDPR/CCPA compliance)

const CONSENT_STORAGE_KEY = 'app_logging_consent';
const CONSENT_EXPIRY_DAYS = 365; // Consent expires after 1 year

class ConsentManager {
  // Check if user has given consent
  hasConsent() {
    try {
      // Check if localStorage is available
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      const consentData = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!consentData) return false;
      
      const consent = JSON.parse(consentData);
      
      // Check if consent has expired
      if (consent.expiryDate) {
        const expiryDate = new Date(consent.expiryDate);
        if (new Date() > expiryDate) {
          // Consent expired, remove it
          this.revokeConsent();
          return false;
        }
      }
      
      return consent.granted === true;
    } catch (error) {
      console.error('Failed to check consent:', error);
      return false;
    }
  }

  // Grant consent
  grantConsent() {
    try {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
      
      const consentData = {
        granted: true,
        grantedDate: new Date().toISOString(),
        expiryDate: expiryDate.toISOString()
      };
      
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
      return true;
    } catch (error) {
      console.error('Failed to grant consent:', error);
      return false;
    }
  }

  // Revoke consent
  revokeConsent() {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Failed to revoke consent:', error);
      return false;
    }
  }

  // Get consent information
  getConsentInfo() {
    try {
      const consentData = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (!consentData) {
        return {
          granted: false,
          grantedDate: null,
          expiryDate: null
        };
      }
      
      return JSON.parse(consentData);
    } catch (error) {
      console.error('Failed to get consent info:', error);
      return {
        granted: false,
        grantedDate: null,
        expiryDate: null
      };
    }
  }

  // Check if consent banner should be shown (hasn't been dismissed recently)
  shouldShowBanner() {
    try {
      const dismissedData = localStorage.getItem('app_consent_banner_dismissed');
      if (!dismissedData) return true;
      
      const dismissed = JSON.parse(dismissedData);
      const dismissedDate = new Date(dismissed.date);
      const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      
      // Show again after 7 days if consent not granted
      if (daysSinceDismissed > 7 && !this.hasConsent()) {
        return true;
      }
      
      return false;
    } catch (error) {
      return true; // Show banner if we can't check
    }
  }

  // Mark banner as dismissed
  dismissBanner() {
    try {
      const dismissedData = {
        date: new Date().toISOString()
      };
      localStorage.setItem('app_consent_banner_dismissed', JSON.stringify(dismissedData));
    } catch (error) {
      console.error('Failed to dismiss banner:', error);
    }
  }
}

// Create singleton instance
const consentManager = new ConsentManager();

export default consentManager;

